import { useState, useEffect } from 'react';
import { buildCotEventXml } from '@vtak/shared';
import { apiClient } from '@/services/api-client';
import { useMapStore } from '@/stores/map-store';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Available marker types for the CoT drop form */
const MARKER_TYPES = [
  { value: 'a-f-G-U-C', label: 'Friendly Ground Unit' },
  { value: 'a-h-G-U-C', label: 'Hostile Ground Unit' },
  { value: 'a-n-G-U-C', label: 'Neutral Ground Unit' },
  { value: 'a-u-G-U-C', label: 'Unknown Ground Unit' },
  { value: 'b-m-p-s-p-i', label: 'Point of Interest' },
] as const;

/** Form submission result feedback */
interface FormResult {
  type: 'success' | 'error';
  text: string;
}

/** Send CoT marker form — auto-fills lat/lng from map clicks */
export function MarkerPanel(): React.JSX.Element {
  const lastClick = useMapStore((s) => s.lastClick);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [callsign, setCallsign] = useState('WebMap');
  const [type, setType] = useState('a-f-G-U-C');
  const [remarks, setRemarks] = useState('');
  const [result, setResult] = useState<FormResult | null>(null);

  useEffect(() => {
    if (lastClick) {
      setLat(lastClick.lat.toFixed(6));
      setLon(lastClick.lng.toFixed(6));
    }
  }, [lastClick]);

  async function handleSend(): Promise<void> {
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);

    if (isNaN(latNum) || isNaN(lonNum)) {
      setResult({ type: 'error', text: 'Enter valid lat/lon or click the map.' });
      return;
    }

    try {
      const xml = buildCotEventXml({
        uid: `WebMap-${Date.now()}`,
        type,
        lat: latNum,
        lon: lonNum,
        callsign: callsign.trim() || 'WebMap',
        remarks: remarks.trim() || undefined,
      });

      const res = await apiClient.sendCot(xml);

      if (res.ok) {
        setResult({ type: 'success', text: `Marker sent: ${callsign}` });
      } else {
        setResult({ type: 'error', text: `Error ${res.status}: ${res.body.substring(0, 120)}` });
      }
    } catch (err) {
      setResult({
        type: 'error',
        text: `Network error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
      <p className="text-xs text-muted-foreground">Click the map to set position, or enter coordinates:</p>

      <div className="flex gap-2">
        <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
          Latitude
          <span className="font-mono text-echo-yellow text-sm">{lat || '—'}</span>
          <Input type="number" step="any" placeholder="e.g. 38.8895" value={lat} onChange={(e) => setLat(e.target.value)} />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
          Longitude
          <span className="font-mono text-echo-yellow text-sm">{lon || '—'}</span>
          <Input type="number" step="any" placeholder="e.g. -77.0353" value={lon} onChange={(e) => setLon(e.target.value)} />
        </label>
      </div>

      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Callsign
        <Input placeholder="e.g. WebUser-1" value={callsign} onChange={(e) => setCallsign(e.target.value)} />
      </label>

      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Type
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          {MARKER_TYPES.map((mt) => (
            <option key={mt.value} value={mt.value}>{mt.label}</option>
          ))}
        </Select>
      </label>

      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Remarks
        <Input placeholder="Optional note" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </label>

      <Button onClick={handleSend}>Send Marker</Button>

      {result && (
        <div
          className={cn(
            'rounded-md p-2 text-xs',
            result.type === 'success' && 'bg-ai/15 text-ai',
            result.type === 'error' && 'bg-destructive/15 text-red-400',
          )}
        >
          {result.text}
        </div>
      )}
    </div>
  );
}
